import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";

import useContactsSearchResultsQuery from "../query/queries/useContactsSearchResultsQuery";
import { contactsQueryKeys } from "../query/queryKeys/queryKeyFactory";
import { selectContactSearchResultsDtoWithAggregation, selectQuotaIsBeingFetched } from "../store/selectors";
import type { ContactsResponseDto } from "../types/dto";

import { useIsContactsDataAvailable } from "./useContactsDataAvailability";

export const useContactSearchResult = <T = ContactsResponseDto>(
    throwOnError?: boolean | ((error: unknown) => boolean),
    select?: (data: ContactsResponseDto) => T,
) => {
    const queryClient = useQueryClient();

    const isQuotaBeingFetched = useSelector(selectQuotaIsBeingFetched);
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const dto = useSelector(selectContactSearchResultsDtoWithAggregation);
    const isContactsDataAvailable = useIsContactsDataAvailable();

    useActionAfterFlagChange(isQuotaBeingFetched, () => {
        void queryClient.invalidateQueries({
            queryKey: contactsQueryKeys.baseTableData(),
        });
    });

    return useContactsSearchResultsQuery(isContactsDataAvailable, activeLeadsType, dto, throwOnError, select);
};

export const useInvalidateContactSearchResultAsync = (): (() => Promise<void>) => {
    const queryClient = useQueryClient();

    return async () => {
        await queryClient.invalidateQueries({
            queryKey: contactsQueryKeys.baseTableData(),
        });
    };
};

export const useInvalidateContactSearchResult = (): (() => void) => {
    const invalidateAsync = useInvalidateContactSearchResultAsync();

    return () => {
        void invalidateAsync();
    };
};
